import type { ComponentProps } from 'react';
import { getDatePlusMinus } from '@einhaus/meteoric-helpers';
import { OBVDivergenceType, OBVEventType, type BarDuration, type ChartMarker, type OBVAnalyzerEvent } from '@einhaus/project314-types';
import type Badge from '@/components/UI/Badge';
import { ALL_OBV_EVENT_TYPES, AUDIT_DURATION_OPTIONS, OBV_EVENT_TYPE_SET } from './constants';
import type { ObvAiAuditResult } from './types';

export const isAuditDuration = (value: string | null): value is BarDuration => {
    if (!value) return false;
    return AUDIT_DURATION_OPTIONS.some((duration) => duration === value);
};

export const parseBooleanSearchParam = (value: string | null, defaultValue: boolean): boolean => {
    if (value === null) return defaultValue;
    if (value === '1' || value.toLowerCase() === 'true') return true;
    if (value === '0' || value.toLowerCase() === 'false') return false;
    return defaultValue;
};

export const parsePositiveInteger = (value: string | null, fallbackValue: number): number => {
    const parsed = Number(value);

    if (!Number.isInteger(parsed) || parsed <= 0) {
        return fallbackValue;
    }

    return parsed;
};

const isObvEventType = (value: string): value is OBVAnalyzerEvent['type'] => {
    return OBV_EVENT_TYPE_SET.has(value);
};

export const parseSelectedEventTypes = (value: string | null): Set<OBVAnalyzerEvent['type']> => {
    if (!value) {
        return new Set(ALL_OBV_EVENT_TYPES);
    }

    const selectedTypes = value
        .split(',')
        .map((item) => item.trim())
        .filter(isObvEventType);

    if (selectedTypes.length === 0) {
        return new Set(ALL_OBV_EVENT_TYPES);
    }

    return new Set(selectedTypes);
};

export const applySelectedEventTypesToSearchParams = (params: URLSearchParams, selectedEventTypes: Set<OBVAnalyzerEvent['type']>): void => {
    if (selectedEventTypes.size === ALL_OBV_EVENT_TYPES.length) {
        params.delete('eventTypes');
        return;
    }

    const orderedTypes = ALL_OBV_EVENT_TYPES.filter((eventType) => selectedEventTypes.has(eventType));
    params.set('eventTypes', orderedTypes.join(','));
};

export const getFormStringValue = (formData: FormData, key: string): string => {
    const value = formData.get(key);
    return typeof value === 'string' ? value : '';
};

export const formatMetric = (value: number | null | undefined, fractionDigits = 2): string => {
    if (value === null || value === undefined || Number.isNaN(value)) {
        return 'n/a';
    }

    return value.toFixed(fractionDigits);
};

export const formatEnumLabel = (value: string | null | undefined): string => {
    if (!value) return 'n/a';

    return value
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/_/g, ' ')
        .replace(/^./, (firstLetter) => firstLetter.toUpperCase());
};

export const formatBooleanFlag = (value: boolean | number | null | undefined): string => {
    if (value === null || value === undefined) return 'n/a';
    return value === true || value === 1 ? 'Yes' : 'No';
};

export const getFocusedChartWindow = (params: {
    duration: BarDuration;
    fallbackEndDateTime: string;
    focusDateTime: string | null;
    windowDays: number;
}): { endDateTime: string; startDateTime: string } => {
    const { duration, fallbackEndDateTime, focusDateTime, windowDays } = params;

    if (!focusDateTime) {
        return {
            endDateTime: fallbackEndDateTime,
            startDateTime: getDatePlusMinus('-', windowDays, 'day', 'ymdhms', 'start', fallbackEndDateTime)
        };
    }

    const focusedWindow =
        duration === '1m'
            ? { beforeDays: 2, afterDays: 1 }
            : duration === '5m'
              ? { beforeDays: 8, afterDays: 2 }
              : duration === '15m'
                ? { beforeDays: 14, afterDays: 4 }
                : duration === '30m'
                  ? { beforeDays: 25, afterDays: 8 }
                  : duration === '60m'
                    ? { beforeDays: 50, afterDays: 15 }
                    : duration === '120m'
                      ? { beforeDays: 70, afterDays: 20 }
                      : duration === '180m'
                        ? { beforeDays: 90, afterDays: 25 }
                        : duration === '240m'
                          ? { beforeDays: 120, afterDays: 30 }
                          : duration === '1d'
                            ? { beforeDays: 180, afterDays: 45 }
                            : { beforeDays: 25, afterDays: 8 };

    return {
        endDateTime: getDatePlusMinus('+', focusedWindow.afterDays, 'day', 'ymdhms', 'end', focusDateTime),
        startDateTime: getDatePlusMinus('-', focusedWindow.beforeDays, 'day', 'ymdhms', 'start', focusDateTime)
    };
};

export const getEventTone = (event: OBVAnalyzerEvent): 'bullish' | 'bearish' | 'neutral' => {
    if (event.divergenceType === OBVDivergenceType.bullish || event.divergenceType === OBVDivergenceType.hiddenBullish) {
        return 'bullish';
    }

    if (event.divergenceType === OBVDivergenceType.bearish || event.divergenceType === OBVDivergenceType.hiddenBearish) {
        return 'bearish';
    }

    const bullishEventTypes: readonly OBVAnalyzerEvent['type'][] = [
        OBVEventType.crossedAboveSignal,
        OBVEventType.bullishBreakoutConfirmed,
        OBVEventType.enteredAccumulation,
        OBVEventType.newLocalHigh
    ];

    const bearishEventTypes: readonly OBVAnalyzerEvent['type'][] = [
        OBVEventType.crossedBelowSignal,
        OBVEventType.bearishBreakdownConfirmed,
        OBVEventType.enteredDistribution,
        OBVEventType.newLocalLow
    ];

    if (bullishEventTypes.includes(event.type)) return 'bullish';
    if (bearishEventTypes.includes(event.type)) return 'bearish';
    return 'neutral';
};

export const getEventMarkerPosition = (event: OBVAnalyzerEvent): ChartMarker['position'] => {
    const tone = getEventTone(event);

    if (tone === 'bullish') return 'belowBar';
    if (tone === 'bearish') return 'aboveBar';
    return 'inBar';
};

export const buildCompactMarkerText = (event: OBVAnalyzerEvent): string => {
    const tone = getEventTone(event);

    const labelByEventType: Record<OBVAnalyzerEvent['type'], string> = {
        [OBVEventType.crossedAboveSignal]: 'OBV Cross Up',
        [OBVEventType.crossedBelowSignal]: 'OBV Cross Down',
        [OBVEventType.bullishDivergenceDetected]: 'OBV Bull Div',
        [OBVEventType.bearishDivergenceDetected]: 'OBV Bear Div',
        [OBVEventType.hiddenBullishDivergence]: 'OBV Hidden Bull Div',
        [OBVEventType.hiddenBearishDivergence]: 'OBV Hidden Bear Div',
        [OBVEventType.divergenceConfirmed]: 'OBV Div Confirmed',
        [OBVEventType.divergenceExpired]: 'OBV Div Expired',
        [OBVEventType.bullishBreakoutConfirmed]: 'OBV Breakout',
        [OBVEventType.bearishBreakdownConfirmed]: 'OBV Breakdown',
        [OBVEventType.enteredAccumulation]: 'OBV Accum',
        [OBVEventType.enteredDistribution]: 'OBV Dist',
        [OBVEventType.newLocalHigh]: 'OBV New High',
        [OBVEventType.newLocalLow]: 'OBV New Low'
    };

    const label = labelByEventType[event.type];

    if (tone === 'bullish') return `${label} Long`;
    if (tone === 'bearish') return `${label} Short`;
    return label;
};

export const buildAssetChartUrl = (params: {
    assetId: number;
    duration: BarDuration;
    endDateTime: string;
    startDateTime: string;
}): string => {
    const queryParams = new URLSearchParams();
    queryParams.set('duration', params.duration);
    queryParams.set('startDateTime', params.startDateTime);
    queryParams.set('endDateTime', params.endDateTime);
    return `/asset/${params.assetId}?${queryParams.toString()}`;
};

export const getToneBadgeVariant = (tone: 'bullish' | 'bearish' | 'neutral'): ComponentProps<typeof Badge>['variant'] => {
    if (tone === 'bullish') return 'success';
    if (tone === 'bearish') return 'danger';
    return 'secondary';
};

export const getToneSummaryLabel = (tone: 'bullish' | 'bearish' | 'neutral'): string => {
    if (tone === 'bullish') return 'Long-side confirmation';
    if (tone === 'bearish') return 'Short-side confirmation';
    return 'Neutral / informational';
};

export const getToneMarkerLabel = (tone: 'bullish' | 'bearish' | 'neutral'): string => {
    if (tone === 'bullish') return 'L';
    if (tone === 'bearish') return 'S';
    return 'N';
};

export const getAiVerdictBadgeVariant = (
    verdict: ObvAiAuditResult['review']['overallVerdict']
): NonNullable<ComponentProps<typeof Badge>['variant']> => {
    if (verdict === 'readyForManualSignoff') return 'success';
    if (verdict === 'needsMajorEdits') return 'danger';
    return 'secondary';
};

export const getAiVerdictLabel = (verdict: ObvAiAuditResult['review']['overallVerdict']): string => {
    if (verdict === 'readyForManualSignoff') return 'Ready For Manual Signoff';
    if (verdict === 'needsMinorEdits') return 'Needs Minor Edits';
    if (verdict === 'needsMajorEdits') return 'Needs Major Edits';
    return 'Inconclusive';
};

export const buildObvAuditRenderUrl = (params: {
    duration: BarDuration;
    endDateTime: string;
    entityId: number;
    focusDateTime: string | null;
    selectedEventTypes: OBVAnalyzerEvent['type'][];
    shouldRemoveAfterHours: boolean;
    variant: 'focused' | 'wide';
    windowDays: number;
}): string => {
    const nextSearchParams = new URLSearchParams();
    nextSearchParams.set('duration', params.duration);
    nextSearchParams.set('endDateTime', params.endDateTime);
    nextSearchParams.set('windowDays', String(params.windowDays));
    nextSearchParams.set('shouldRemoveAfterHours', params.shouldRemoveAfterHours ? '1' : '0');
    nextSearchParams.set('variant', params.variant);
    nextSearchParams.set('eventTypes', params.selectedEventTypes.join(','));

    if (params.focusDateTime) {
        nextSearchParams.set('focusDateTime', params.focusDateTime);
    }

    return `/tools/obvAudit/render/${String(params.entityId)}?${nextSearchParams.toString()}`;
};
